import { t } from '../i18n'
import { readJSON, removeKey, writeJSON } from './storage'

/* The daily mini crossword (pres: api/crossword.py, docs/CROSSWORD_API.md).
   The API sends the solution rows ("SOVA#", '#' = black) and the clues with
   where each word starts; here that becomes words with their cells. */
export const ACROSS = 'across'
export const DOWN = 'down'

export const cellKey = (r, c) => `${r},${c}`

export function buildPuzzle(data) {
  const { rows, cols, grid } = data
  const isBlack = (r, c) => r < 0 || c < 0 || r >= rows || c >= cols || grid[r][c] === '#'
  const words = []
  ;[ACROSS, DOWN].forEach((dir) =>
    [...(data.clues[dir] || [])]
      .sort((a, b) => a.number - b.number)
      .forEach((clue) => {
        const cells = Array.from({ length: clue.length }, (_, i) =>
          dir === ACROSS ? [clue.row, clue.col + i] : [clue.row + i, clue.col]
        )
        words.push({
          dir,
          number: clue.number,
          text: clue.text,
          cells,
          key: `${dir}-${clue.number}`,
        })
      })
  )
  const numbers = {}
  words.forEach((w) => {
    numbers[cellKey(...w.cells[0])] = w.number
  })
  const whites = []
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++) if (!isBlack(r, c)) whites.push([r, c])
  return { ...data, isBlack, words, numbers, whites, solution: (r, c) => grid[r][c] }
}

export const wordAt = (puzzle, r, c, dir) =>
  puzzle.words.find((w) => w.dir === dir && w.cells.some(([y, x]) => y === r && x === c)) || null

export const other = (dir) => (dir === ACROSS ? DOWN : ACROSS)

/* Letters a square takes: the BCS alphabet, one letter per square (the
   generator leaves out lj, nj, dž). */
const LETTER = /^[a-pr-vzčćđšž]$/i
export const toLetter = (key) => (LETTER.test(key || '') ? key.toLocaleUpperCase('hr') : null)

export const KEYBOARD_ROWS = ['ERTZUIOPŠĐ', 'ASDFGHJKLČĆ', 'ŽCVBNM']

/* 72 -> "1:12", 3725 -> "1:02:05" */
export function formatTime(secs) {
  const s = Math.max(0, Math.floor(secs || 0))
  const pad = (n) => String(n).padStart(2, '0')
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  return h ? `${h}:${pad(m)}:${pad(s % 60)}` : `${m}:${pad(s % 60)}`
}

export const formatCount = (n) => Number(n || 0).toLocaleString('de-DE') // 1.248

/* Dates come as "2026-10-20" (a day in Sarajevo); read them as that day. */
export function parseDay(day) {
  const [y, m, d] = String(day).split('-').map(Number)
  return new Date(y, m - 1, d)
}

export const toDay = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`

const list = (key) => t(key).split(',')

/* "20. 10." */
export const shortDate = (day) => {
  const d = parseDay(day)
  return `${d.getDate()}. ${d.getMonth() + 1}.`
}

/* "Utorak, 20. 10." */
export function weekdayDate(day) {
  const d = parseDay(day)
  const weekday = list('crossword.weekdays')[d.getDay()]
  return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${shortDate(day)}`
}

/* "utorak, 20. oktobar" */
export function longDate(day) {
  const d = parseDay(day)
  return `${list('crossword.weekdays')[d.getDay()]}, ${d.getDate()}. ${
    list('crossword.monthsOf')[d.getMonth()]
  }`
}

/* "Oktobar 2026" */
export function monthTitle(first) {
  const name = list('crossword.months')[first.getMonth()]
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${first.getFullYear()}`
}

export const weekdayInitials = () => list('crossword.weekdayInitials') // P U S Č P S N

/* ------------------------------ progress ------------------------------
   Kept in this browser per day, so a reload (or coming back later) carries
   on where the reader stopped: { entries: {"0,1": "O"}, revealed: {"0,1":
   true}, secs, started, solved, submitted }. */
const PROGRESS_PREFIX = 'instapres.crossword.'
const KEEP_DAYS = 62

export const emptyProgress = () => ({
  entries: {},
  revealed: {},
  secs: 0,
  started: false,
  solved: false,
  submitted: false,
})

export function loadProgress(day) {
  const saved = readJSON(PROGRESS_PREFIX + day, null)
  if (!saved || typeof saved !== 'object') return emptyProgress()
  return { ...emptyProgress(), ...saved }
}

export function saveProgress(day, progress) {
  writeJSON(PROGRESS_PREFIX + day, progress)
}

/* Old days' progress isn't needed once the server has the result. */
export function pruneProgress(today) {
  try {
    const cutoff = parseDay(today).getTime() - KEEP_DAYS * 86400000
    Object.keys(window.localStorage)
      .filter((k) => k.startsWith(PROGRESS_PREFIX))
      .forEach((k) => {
        const time = parseDay(k.slice(PROGRESS_PREFIX.length)).getTime()
        if (Number.isNaN(time) || time < cutoff) removeKey(k)
      })
  } catch (e) {
    // storage blocked: nothing to prune
  }
}

export const isSolvedBy = (puzzle, entries) =>
  puzzle.whites.every(([r, c]) => entries[cellKey(r, c)] === puzzle.solution(r, c))

/* The rows the server checks a solve against. */
export const entryRows = (puzzle, entries) =>
  puzzle.grid.map((row, r) =>
    row
      .split('')
      .map((ch, c) => (ch === '#' ? '#' : entries[cellKey(r, c)] || ' '))
      .join('')
  )
